import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Mail } from "lucide-react";

interface ResendInviteModalProps {
  user: any;
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onResend: (user: any) => void;
  onRepair: (user: any) => void;
}

export default function ResendInviteModal({ user, isOpen, isSubmitting, onClose, onResend, onRepair }: ResendInviteModalProps) {
  const [confirmRepair, setConfirmRepair] = useState(false);
  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Send setup email
          </DialogTitle>
          <DialogDescription>
            Send account setup instructions to <strong>{user.email}</strong> so they can set up their login.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 flex gap-2">
            <Mail className="h-4 w-4 mt-1 flex-shrink-0" />
            <p>
              Pending accounts receive an invitation. Existing confirmed accounts receive a password reset email. The existing account is preserved. Use the latest email; expiry follows your organisation’s authentication settings.
            </p>
          </div>
          <div className="text-sm text-muted-foreground">
            <p><strong>Name:</strong> {user.name || 'Unknown'}</p>
            <p><strong>Role:</strong> {user.role}</p>
            {Array.isArray(user.roles) && user.roles.length > 0 && (
              <p><strong>Roles:</strong> {user.roles.join(', ')}</p>
            )}
          </div>
          <div className="border-t pt-3 text-sm">
            <p>If setup reports that the linked login is missing, an administrator can repair it while keeping this user’s work history.</p>
            {confirmRepair ? <div className="mt-2 space-y-2">
              <p>Repair the missing login for {user.email}? The user will need a setup email and a new password.</p>
              <Button variant="outline" disabled={isSubmitting} onClick={() => onRepair(user)}>Confirm repair login</Button>
              <Button variant="ghost" disabled={isSubmitting} onClick={() => setConfirmRepair(false)}>Cancel repair</Button>
            </div> : <Button variant="link" disabled={isSubmitting} onClick={() => setConfirmRepair(true)}>Repair login</Button>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => onResend(user)} disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Send setup email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
